import * as React from "react";
import clsx from "clsx";
import { ChevronDown, FileText, Filter, Folder, FolderOpen, Search } from "lucide-react";

import type { BusinessReport } from "@/modules/snapshots/businessReports.types";
import { isSnapshotTemplate } from "@/modules/snapshots/businessReports.types";

const isNotSnapshotTemplate = (template: BusinessReport): boolean => !isSnapshotTemplate(template);
import { BusinessReportSelectFileModal } from "@/modules/snapshots/modals/selectFileModal";
import { useGetSubcollectionsQuery } from "@/modules/snapshots/snapshotCollection.api";
import type { SnapshotCollection, Subcollection } from "@/modules/snapshots/snapshotCollection.types";
import type {
  SnapshotTemplateGroup,
  SnapshotTemplateNavigatorModel,
} from "@/modules/snapshots/snapshotCollections/dashboardUtils";
import { formatRelativeDate } from "@/modules/snapshots/snapshotCollections/utils/dateFormatting";

import { CollectionActionsMenu } from "../collectionActionsMenu";

interface Props {
  activeCollectionId?: string;
  model: SnapshotTemplateNavigatorModel;
  search: string;
  onSearchChange: (search: string) => void;
  onOpenCollection: (collection: SnapshotCollection) => void;
  onOpenSubcollection: (collection: SnapshotCollection, subcollectionId: string) => void;
}

interface TemplateSectionProps {
  activeCollectionId?: string;
  className?: string;
  groups: SnapshotTemplateGroup[];
  label: string;
  onOpenCollection: (collection: SnapshotCollection) => void;
  onOpenSubcollection: (collection: SnapshotCollection, subcollectionId: string) => void;
}

interface TemplateCardProps {
  activeCollectionId?: string;
  group: SnapshotTemplateGroup;
  onOpenCollection: (collection: SnapshotCollection) => void;
  onOpenSubcollection: (collection: SnapshotCollection, subcollectionId: string) => void;
}

interface DashboardCollectionRowProps {
  activeCollectionId?: string;
  collection: SnapshotCollection;
  onOpenCollection: (collection: SnapshotCollection) => void;
  onOpenSubcollection: (collection: SnapshotCollection, subcollectionId: string) => void;
}

const emptySubcollections: Subcollection[] = [];

export const SnapshotCollectionsDashboardSidebar: React.FC<Props> = ({
  activeCollectionId,
  model,
  search,
  onSearchChange,
  onOpenCollection,
  onOpenSubcollection,
}) => {
  const [selectedTemplateForCreate, setSelectedTemplateForCreate] = React.useState<BusinessReport | null>(null);
  const hasTemplateGroups =
    model.primaryGroups.length > 0 || model.secondaryGroups.length > 0 || model.categorySections.length > 0;

  const handleTemplateClick = (template: BusinessReport) => {
    if (isNotSnapshotTemplate(template)) {
      return;
    }
    setSelectedTemplateForCreate(template);
  };

  const handleCloseModal = () => {
    setSelectedTemplateForCreate(null);
  };

  return (
    <aside
      aria-label="Collections navigator"
      className="min-h-[calc(100vh-8rem)] w-full border-r border-[#ddd5cb] pr-6"
    >
      <div className="mb-4 flex gap-2">
        <label className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            aria-label="Search templates and collections"
            name="snapshot-collections-sidebar-search"
            placeholder="Search templates and collections"
            autoComplete="off"
            className="h-10 w-full rounded-md border border-[#ddd5cb] bg-white pl-10 pr-3 text-sm font-medium text-gray-900 outline-none placeholder:text-gray-400 focus:border-desert-sand-700 focus:ring-2 focus:ring-desert-sand-200"
            type="search"
          />
        </label>
        <button
          type="button"
          disabled
          className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border border-[#ddd5cb] bg-white text-gray-700 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Filters unavailable"
        >
          <Filter className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-6 pt-2">
        {model.primaryGroups.length > 0 && (
          <TemplateSection
            label={model.primaryLabel}
            groups={model.primaryGroups}
            activeCollectionId={activeCollectionId}
            onOpenCollection={onOpenCollection}
            onOpenSubcollection={onOpenSubcollection}
          />
        )}

        {model.categorySections.map(section => (
          <TemplateSection
            key={section.label}
            label={section.label}
            groups={section.groups}
            activeCollectionId={activeCollectionId}
            onOpenCollection={onOpenCollection}
            onOpenSubcollection={onOpenSubcollection}
          />
        ))}

        {model.categorySections.length === 0 && model.secondaryGroups.length > 0 && (
          <TemplateSection
            label={model.secondaryLabel}
            groups={model.secondaryGroups}
            activeCollectionId={activeCollectionId}
            onOpenCollection={onOpenCollection}
            onOpenSubcollection={onOpenSubcollection}
          />
        )}

        {!hasTemplateGroups && (
          <div className="rounded-lg border border-dashed border-[#ddd5cb] bg-white/50 px-4 py-5 text-sm text-gray-500">
            No templates in this section
          </div>
        )}
      </div>

      <section className="mt-[42px] overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb]/75 shadow-none">
        <div className="flex h-12 items-center gap-2 px-4 text-sm font-semibold text-gray-800">
          <ChevronDown className="h-4 w-4 rotate-90 text-gray-500" />
          <span className="min-w-0 truncate">Empty Templates</span>
          <span className="ml-auto inline-flex h-6 min-w-7 items-center justify-center rounded-full bg-[#eadfd3] px-2 text-xs font-semibold text-gray-700">
            {model.emptyTemplates.length}
          </span>
        </div>
        <div className="border-t border-[#ebe5de] px-4 py-2 text-sm text-gray-700">
          {model.emptyTemplates.slice(0, 6).map(template => (
            <button
              key={template.id}
              type="button"
              onClick={() => handleTemplateClick(template)}
              disabled={isNotSnapshotTemplate(template)}
              className="block w-full border-t border-[#ebe5de] py-2.5 text-left first:border-t-0 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              <span className="block truncate">{template.name}</span>
            </button>
          ))}
          {!model.emptyTemplates.length && <p className="py-2 text-sm text-gray-500">No empty templates</p>}
        </div>
      </section>

      {selectedTemplateForCreate && (
        <BusinessReportSelectFileModal businessReport={selectedTemplateForCreate} onClose={handleCloseModal} />
      )}
    </aside>
  );
};

const TemplateSection: React.FC<TemplateSectionProps> = ({
  activeCollectionId,
  className,
  groups,
  label,
  onOpenCollection,
  onOpenSubcollection,
}) => (
  <section className={className}>
    <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.04em] text-gray-500">{label}</h2>
    <div className="grid gap-3">
      {groups.map(group => (
        <TemplateCard
          key={group.template.id}
          group={group}
          activeCollectionId={activeCollectionId}
          onOpenCollection={onOpenCollection}
          onOpenSubcollection={onOpenSubcollection}
        />
      ))}
    </div>
  </section>
);

const TemplateCard: React.FC<TemplateCardProps> = ({
  activeCollectionId,
  group,
  onOpenCollection,
  onOpenSubcollection,
}) => {
  const containsActiveCollection = group.collections.some(collection => collection.id === activeCollectionId);
  const [isExpanded, setIsExpanded] = React.useState(group.isPinned || containsActiveCollection);
  const collectionCount = group.collections.length;

  React.useEffect(() => {
    if (containsActiveCollection) {
      setIsExpanded(true);
    }
  }, [containsActiveCollection]);

  const toggleExpanded = () => {
    setIsExpanded(current => !current);
  };

  return (
    <article
      className={clsx(
        "overflow-hidden rounded-lg border border-l-[5px] border-[#ddd5cb] border-l-[#c9b39c] bg-[#fffdfb]/80 shadow-none",
        {
          "bg-[#fbf7f2]": isExpanded || containsActiveCollection,
        },
      )}
    >
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        className="grid w-full grid-cols-[40px_minmax(0,1fr)_22px] items-center gap-3 px-3.5 py-3.5 text-left transition-colors hover:bg-[#f7f2ec]"
      >
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#07142d] text-white">
          <FileText className="h-[18px] w-[18px]" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-gray-900">{group.template.name}</span>
          <span className="mt-0.5 block truncate text-xs font-medium text-gray-500">
            {group.snapshots} snapshots across {collectionCount} {collectionCount === 1 ? "collection" : "collections"}
          </span>
        </span>

        <ChevronDown
          className={clsx("h-4 w-4 flex-shrink-0 text-gray-500 transition-transform", {
            "-rotate-90": !isExpanded,
          })}
        />
      </button>

      {isExpanded && (
        <div className="border-t border-[#ebe5de] bg-white px-3.5 py-3.5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.04em] text-gray-500">Collections</p>
          <div className="grid gap-2">
            {group.collections.map(collection => (
              <DashboardCollectionRow
                key={collection.id}
                activeCollectionId={activeCollectionId}
                collection={collection}
                onOpenCollection={onOpenCollection}
                onOpenSubcollection={onOpenSubcollection}
              />
            ))}
          </div>
        </div>
      )}
    </article>
  );
};

const DashboardCollectionRow: React.FC<DashboardCollectionRowProps> = ({
  activeCollectionId,
  collection,
  onOpenCollection,
  onOpenSubcollection,
}) => {
  const [isSubcollectionsExpanded, setIsSubcollectionsExpanded] = React.useState(false);
  // The chevron comes from the list payload; the subcollections themselves are fetched only once the
  // row is expanded, so a template group with hundreds of collections issues no request on mount.
  const hasSubcollections = Boolean(collection.has_subcollections);
  const { data: subcollections = emptySubcollections, isLoading: isLoadingSubcollections } = useGetSubcollectionsQuery(
    { snapshot_collection_id: collection.id },
    { skip: !isSubcollectionsExpanded },
  );
  const isActive = collection.id === activeCollectionId;

  const handleSubcollectionsToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsSubcollectionsExpanded(current => !current);
  };

  return (
    <div
      className={clsx("group overflow-hidden rounded-md transition-colors", {
        "bg-[#f3ece4]": isActive,
        "bg-[#fbf8f4]": isSubcollectionsExpanded,
      })}
    >
      <div className="flex items-center gap-1 rounded-md transition-colors hover:bg-[#f7f2ec]">
        {hasSubcollections ? (
          <button
            type="button"
            onClick={handleSubcollectionsToggle}
            aria-expanded={isSubcollectionsExpanded}
            aria-label={isSubcollectionsExpanded ? "Collapse sub-collections" : "Expand sub-collections"}
            className="ml-1 flex h-8 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-[#ebe5de]"
          >
            <ChevronDown
              className={clsx("h-3.5 w-3.5 -rotate-90 transition-transform", {
                "rotate-0": isSubcollectionsExpanded,
              })}
            />
          </button>
        ) : (
          <span className="ml-1 h-8 w-7 flex-shrink-0" />
        )}

        <button
          type="button"
          onClick={() => onOpenCollection(collection)}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-md py-2 pl-0 pr-2 text-left"
        >
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#f1e2ce] text-desert-sand-700">
            <Folder className="h-4 w-4" />
          </span>

          <span className="min-w-0 flex-1">
            <span className="block truncate text-base font-semibold text-gray-900">{collection.name}</span>
            <span className="block truncate text-sm font-medium text-gray-500">
              {formatRelativeDate(collection.created_at)}
            </span>
          </span>

          <span className="inline-flex h-7 min-w-[36px] flex-shrink-0 items-center justify-center rounded-full bg-[#07142d] px-2 text-sm font-bold text-white">
            {collection.snapshots}
          </span>
        </button>

        <CollectionActionsMenu
          collection={collection}
          triggerClassName="mr-2 opacity-60 transition-opacity hover:opacity-100 group-hover:opacity-100"
        />
      </div>

      {hasSubcollections && isSubcollectionsExpanded && (
        <div className="border-t border-[#ebe5de] px-2 py-1.5">
          {isLoadingSubcollections && (
            <p className="py-1.5 pl-11 pr-2 text-sm text-gray-500" aria-live="polite">
              Loading sub-collections…
            </p>
          )}
          {!isLoadingSubcollections && subcollections.length === 0 && (
            <p className="py-1.5 pl-11 pr-2 text-sm text-gray-500">No sub-collections</p>
          )}
          {subcollections.map(subcollection => (
            <button
              key={subcollection.id}
              type="button"
              onClick={() => onOpenSubcollection(collection, subcollection.id)}
              className="flex w-full items-center gap-2.5 rounded-md py-1.5 pl-11 pr-2 text-left text-sm text-gray-700 transition-colors hover:bg-[#f7f2ec]"
            >
              <FolderOpen className="h-3.5 w-3.5 flex-shrink-0 text-desert-sand-700" />
              <span className="min-w-0 flex-1 truncate">{subcollection.name}</span>
              <span className="inline-flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full bg-[#ebe5de] px-1.5 text-[10px] font-semibold leading-none text-gray-700">
                {subcollection.member_count}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
